import { REALTIME_EVENT_MAP } from '../commands/command-map.js';
import { DispatchEvent } from './DispatchEvent.js';
import type { IRealtimeEvent } from './RealtimeEvent.js';
import { Fluid } from './fluid.js';

export class DispatchEventFactory {
  public static fromEvent<T extends IRealtimeEvent<any, any>>(event: unknown): DispatchEvent<T> {
    if (!event || typeof event !== 'object') {
      throw new TypeError(`Expected event to be an object, got: ${typeof event}`);
    }

    if (!('f' in event)) {
      throw new TypeError('Expected the fluid (f key) to be present');
    }

    if (!('e' in event)) {
      throw new TypeError('Expected the event (e key) to be present');
    }

    if (!Fluid.isValid(event.f)) {
      throw new TypeError('Invalid fluid provided');
    }

    if (!event.e || typeof event.e !== 'object' || !('op' in event.e)) {
      throw new TypeError('Invalid event provided');
    }

    if (typeof event.e.op !== 'string') {
      throw new TypeError('Invalid event type provided');
    }

    if (!('data' in event.e)) {
      throw new TypeError('Invalid event data provided');
    }

    if (!event.e.data || typeof event.e.data !== 'object') {
      throw new TypeError('Invalid event data provided');
    }

    const eventClass = REALTIME_EVENT_MAP[event.e.op as unknown as keyof typeof REALTIME_EVENT_MAP];

    if (!eventClass) {
      throw new TypeError(`Unknown event type: ${event.e.op}`);
    }

    try {
      const parsedEvent = eventClass.schema ? new eventClass(event.e.data as any) : new eventClass();
      return new DispatchEvent<T>(event.f, parsedEvent as unknown as T);
    } catch (error) {
      throw new TypeError(`Failed to parse event: ${error}`);
    }
  }

  public static toDispatchEvent<E extends IRealtimeEvent<any, any>>(fluid: `fluid:v1:account:${string}`, event: E) {
    return new DispatchEvent<E>(fluid, event);
  }
}
